import React, { ChangeEvent, useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";

const isHttpOk = (status: StatusCode | number): boolean => Number(status) === Number(StatusCode.OK);
import { ICustomer } from "redux/reducers/models/customer";
import { showError } from "redux/actions/ui";
import { ITournamentParticipant } from "redux/reducers/models/tournament";
import { customerErrorMessage } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";
import FormLayout from "components/form/FormLayout";
import Page from "components/page/Page";
import Sheet from "components/sheet/Sheet";
import Tabs from "components/tabs/Tabs";
import Input from "components/form/input/Input";
import { Select } from "components/select";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import Checkbox from "components/form/checkbox/Checkbox";
import { Badge } from "components/badge/Badge";
import DataTable from "../../../../../../../components/dataTable/DataTable";
import Portal from "elements/Portal";

import { GetTournamentOrganizer, PostTournamentOrganizer } from "api/rpc/2024-04/facilityAdmin/tournament/organizer";
import { AddPlayerToTournament, GetTournamentPlayers } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetCustomer, PostCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";

export default function TournamentOrganizers() {
  const { t } = useTranslation();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const dispatch = useAppDispatch();
  const { Option } = Select;

  const [players, setPlayers] = useState<ICustomer[]>([]);
  const [organizers, setOrganizers] = useState<ICustomer[]>(undefined);
  const [facilityCustomers, setFacilityCustomers] = useState<ICustomer[]>([]);

  const [newOrganizerModal, setNewOrganizerModal] = useState({
    modalOpen: false,
    addAsParticipant: false,
    tabIndex: 0,
    search: "",
    selectedCustomer: undefined as ICustomer,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // Load players + organizers on first load
  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadOrganizers(source.token);
    void loadTournamentPlayers(source.token); // Only being used to trigger checkbox disable on Add Organizer modal

    return () => {
      source.cancel();
    };
  }, []);

  // Search facility customers only when search changes
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (newOrganizerModal.search === newOrganizerModal.selectedCustomer?.full_name) {
      return;
    }
    if (newOrganizerModal.search.length === 0) {
      setFacilityCustomers([]);
      return;
    }

    void searchFacilityCustomers(newOrganizerModal.search, source.token);

    return () => {
      source.cancel();
    };
  }, [newOrganizerModal.search]);

  // addAsParticipant checkbox disabled if chosen customer is already in the tournament
  useEffect(() => {
    if (players && newOrganizerModal.selectedCustomer) {
      if (players.find(val => val.id === newOrganizerModal.selectedCustomer.id)) {
        setNewOrganizerModal(prev => ({ ...prev, addAsParticipant: false }));
      }
    }
  }, [players, newOrganizerModal.selectedCustomer]);

  // GET tournament organizers.  Sets ONLY res.data customer value into state.
  async function loadOrganizers(token?: CancelToken) {
    if (organizers !== undefined) {
      setOrganizers(undefined);
    }

    const res = await GetTournamentOrganizer({ tournament_id: Number(tournamentId) }, token ? false : true, token);
    if (!isHttpOk(res.status)) {
      setOrganizers([]);
      return;
    }

    if (res.data.length !== 0) {
      setOrganizers(res.data.map(val => val.customer));
    } else {
      setOrganizers([]);
    }
  }

  // GET tournament players.  Sets ONLY res.data customer value into state
  async function loadTournamentPlayers(token?: CancelToken) {
    const params = {
      tournament_id: tournamentId,
    };

    const tournamentPlayersRes = await GetTournamentPlayers(params, false, token);

    if (!isHttpOk(tournamentPlayersRes.status)) {
      dispatch(showError(t("secure.facility.tournament.settings.organizers.tournament_organizers.002")));
      return;
    }

    const participantRows = tournamentPlayersRes.data as ITournamentParticipant[];
    setPlayers(participantRows.map(player => player.customer));
  }

  async function searchFacilityCustomers(search: string, token?: CancelToken) {
    if (facilityCustomers !== undefined) {
      setFacilityCustomers(undefined);
    }

    const customerRes = await GetCustomer({ search: search }, false, token);

    if (token?.reason) {
      return;
    }

    if (!isHttpOk(customerRes.status)) {
      setFacilityCustomers([]);
      return;
    }

    setFacilityCustomers(customerRes.data);
  }

  // Calls addUserAsOrganizer if response is OK
  async function addPlayerToTournament(customer: ICustomer) {
    const params = {
      tournament_id: Number(tournamentId),
      customer_id: customer.id,
    };

    const res = await AddPlayerToTournament(params, true);

    if (!isHttpOk(res.status)) {
      dispatch(showError(t("secure.facility.tournament.settings.organizers.tournament_organizers.003")));
      return;
    }

    // add player as an organizer
    void addUserAsOrganizer(customer);
  }

  // Add 1 organizer to the tournament
  async function addUserAsOrganizer(customer: ICustomer) {
    const res = await PostTournamentOrganizer({ tournament_id: Number(tournamentId), user_id: customer.id }, true);

    if (!isHttpOk(res.status)) {
      return;
    }

    setOrganizers(prev => prev.concat(customer)); // Set the organizer without calling API

    setNewOrganizerModal({
      modalOpen: false,
      addAsParticipant: false,
      tabIndex: 0,
      selectedCustomer: undefined,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      search: "",
    });
  }

  async function saveNewPlayer() {
    const newPlayerRes = await PostCustomer(
      {
        first_name: newOrganizerModal.firstName,
        last_name: newOrganizerModal.lastName,
        email: newOrganizerModal.email || null,
        phone: newOrganizerModal.phone || null,
      },
      true,
    );
    if (!isHttpOk(newPlayerRes.status)) {
      const errMsg = newPlayerRes.data?.message;
      dispatch(showError(customerErrorMessage(t, typeof errMsg === "string" ? errMsg : "")));
      return;
    }
    const createdCustomer = newPlayerRes.data as ICustomer;
    if (newOrganizerModal.addAsParticipant) {
      void addPlayerToTournament(createdCustomer);
    } else {
      void addUserAsOrganizer(createdCustomer);
    }
  }

  function handleModalOk(customer?: ICustomer) {
    setNewOrganizerModal(prev => ({ ...prev, search: "" }));

    switch (newOrganizerModal.tabIndex) {
      case 0: {
        // Only add customer to the tournament if checkbox checked
        if (newOrganizerModal.addAsParticipant) {
          void addPlayerToTournament(customer);
        } else {
          void addUserAsOrganizer(customer);
        }
        break;
      }
      case 1: {
        // Save a brand new player to the tournament
        void saveNewPlayer();
        break;
      }
    }
  }

  const primaryAction = {
    content: t("secure.facility.tournament.settings.organizers.tournament_organizers.004"),
    action: () => setNewOrganizerModal(prev => ({ ...prev, modalOpen: true })),
  };

  return (
    <Page
      title={t("secure.facility.tournament.settings.organizers.tournament_organizers.001")}
      primaryAction={primaryAction}
    >
      <DataTable columns={tableColumns(t)} loading={organizers === undefined}>
        {organizers
          ? organizers.map(player => {
              return (
                <tr key={player?.id}>
                  <td>{player.full_name}</td>
                </tr>
              );
            })
          : null}
      </DataTable>

      <Portal isMounted={newOrganizerModal.modalOpen}>
        <Sheet
          title={t("secure.facility.tournament.settings.organizers.tournament_organizers.005")}
          open={true}
          overflow
          onCancel={() =>
            setNewOrganizerModal({
              modalOpen: false,
              addAsParticipant: false,
              tabIndex: 0,
              selectedCustomer: undefined,
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              search: "",
            })
          }
          onOk={() => handleModalOk(newOrganizerModal.selectedCustomer && newOrganizerModal.selectedCustomer)}
          okText={
            newOrganizerModal.tabIndex === 0
              ? t("secure.facility.tournament.settings.organizers.tournament_organizers.004")
              : newOrganizerModal.tabIndex === 1
              ? t("secure.facility.tournament.settings.organizers.tournament_organizers.006")
              : null
          }
          size="small"
          okDisabled={
            (newOrganizerModal.tabIndex === 0 && newOrganizerModal.selectedCustomer === undefined) ||
            (newOrganizerModal.tabIndex === 1 &&
              (newOrganizerModal.firstName === "" ||
                newOrganizerModal.lastName === "" ||
                (newOrganizerModal.email === "" && newOrganizerModal.phone === "")))
          }
        >
          <Tabs
            tabs={modalTabs(t)}
            selected={newOrganizerModal.tabIndex}
            onSelect={index =>
              setNewOrganizerModal(prev => ({ ...prev, tabIndex: index, selectedCustomer: undefined, search: "" }))
            }
          >
            <div style={{ paddingTop: "16px" }}>
              {newOrganizerModal.tabIndex === 0 && (
                <>
                  {newOrganizerModal.selectedCustomer ? (
                    <GolferCard
                      closable
                      removeGolfer={() =>
                        setNewOrganizerModal(prev => ({ ...prev, selectedCustomer: undefined }))
                      }
                      email={newOrganizerModal.selectedCustomer?.email}
                      name={newOrganizerModal.selectedCustomer?.full_name}
                      memberCode={newOrganizerModal.selectedCustomer?.member_code}
                      customerType={newOrganizerModal.selectedCustomer?.customer_type}
                      phone={newOrganizerModal.selectedCustomer?.phone}
                    />
                  ) : (
                    <Select
                      showSearch
                      placeholder={t("secure.facility.tournament.settings.organizers.tournament_organizers.007")}
                      onSearch={(val: string) =>
                        setNewOrganizerModal(prev => ({
                          ...prev,
                          search: val,
                          selectedCustomer: val.length === 0 ? undefined : prev.selectedCustomer,
                        }))
                      }
                      searchValue={newOrganizerModal.search}
                      onChange={(id: number, customer: ICustomer) =>
                        setNewOrganizerModal(prev => ({ ...prev, selectedCustomer: customer }))
                      }
                      loading={typeof facilityCustomers === "undefined"}
                      showDropDownOnFocus
                      allowClear
                    >
                      {facilityCustomers ? (
                        facilityCustomers
                          .filter(val => !organizers.find(cust => val.id === cust.id))
                          .map(customer => (
                            <Option key={customer.id} value={customer.id} name={customer.full_name} extraValues={customer}>
                              <div className="flex justify-between">
                                <div>
                                  <div className="text-semibold text-lg">{customer?.full_name}</div>
                                  <div className="text-sm text-gray-500">{customer.customer_type}</div>
                                  <div className="text-sm text-gray-500">{customer.email}</div>
                                  <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                                </div>

                                <div className="flex flex-column justify-between">
                                  <div className="flex flex-column gap-2">
                                    {customer.has_high_priority_notes ? (
                                      <div
                                        className="font-medium text-error self-end gap-2 flex"
                                        style={{ alignItems: "center" }}
                                        onClick={e => {
                                          e.stopPropagation();
                                          window.open(`/admin/customers/${String(customer.id)}/notes`);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                        <p>{t("Click to view notes")}</p>
                                      </div>
                                    ) : (
                                      <div />
                                    )}
                                    {customer?.banned ? (
                                      <div className="ml-auto">
                                        <Badge size="small" type="warning">
                                          {t("Banned")}
                                        </Badge>
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="font-medium text-base text-gray-500 self-end">
                                    {customer.member_code}
                                  </div>
                                </div>
                              </div>
                            </Option>
                          ))
                      ) : (
                        <Option value={"loading"}></Option>
                      )}
                    </Select>
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "16px" }}>
                    <Checkbox
                      size="medium"
                      label={t("secure.facility.tournament.settings.organizers.tournament_organizers.008")}
                      checked={newOrganizerModal.addAsParticipant}
                      onChange={() =>
                        setNewOrganizerModal(prev => ({ ...prev, addAsParticipant: !prev.addAsParticipant }))
                      }
                      disabled={
                        newOrganizerModal.selectedCustomer !== undefined
                          ? players?.find(val => val.id === newOrganizerModal.selectedCustomer.id)
                            ? true
                            : false
                          : false
                      }
                    />
                  </div>
                </>
              )}

              {newOrganizerModal.tabIndex === 1 && (
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      label={t("secure.facility.tournament.settings.organizers.tournament_organizers.009")}
                      placeholder={t("secure.facility.tournament.settings.organizers.tournament_organizers.009")}
                      value={newOrganizerModal.firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewOrganizerModal(prev => ({ ...prev, firstName: e.target.value }))
                      }
                    />
                    <Input
                      label={t("secure.facility.tournament.settings.organizers.tournament_organizers.010")}
                      placeholder={t("secure.facility.tournament.settings.organizers.tournament_organizers.010")}
                      value={newOrganizerModal.lastName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewOrganizerModal(prev => ({ ...prev, lastName: e.target.value }))
                      }
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input
                      label={t("secure.facility.tournament.settings.organizers.tournament_organizers.011")}
                      placeholder={t("secure.facility.tournament.settings.organizers.tournament_organizers.011")}
                      value={newOrganizerModal.email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewOrganizerModal(prev => ({ ...prev, email: e.target.value }))
                      }
                    />
                    <Input
                      label={t("secure.facility.tournament.settings.organizers.tournament_organizers.012")}
                      placeholder={t("secure.facility.tournament.settings.organizers.tournament_organizers.012")}
                      value={newOrganizerModal.phone}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewOrganizerModal(prev => ({ ...prev, phone: e.target.value }))
                      }
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Checkbox
                      size="medium"
                      label={t("secure.facility.tournament.settings.organizers.tournament_organizers.008")}
                      checked={newOrganizerModal.addAsParticipant}
                      onChange={() =>
                        setNewOrganizerModal(prev => ({ ...prev, addAsParticipant: !prev.addAsParticipant }))
                      }
                      disabled={
                        newOrganizerModal.selectedCustomer &&
                        players?.find(val => val.id === newOrganizerModal.selectedCustomer.id)
                          ? true
                          : false
                      }
                    />
                  </FormLayout.Group>
                </FormLayout>
              )}
            </div>
          </Tabs>
        </Sheet>
      </Portal>
    </Page>
  );
}

const tableColumns = (t: TFunction<"translation", undefined>) => {
  return [{ label: t("secure.facility.tournament.settings.organizers.tournament_organizers.013") }];
};

const modalTabs = (t: TFunction<"translation", undefined>) => {
  return [
    { id: "search-organizer", content: t("secure.facility.tournament.settings.organizers.tournament_organizers.014") },
    { id: "new-organizer", content: t("secure.facility.tournament.settings.organizers.tournament_organizers.015") },
  ];
};
